import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Typography,
	Card,
	CardContent,
	Grid,
	Box,
	Chip,
	Button,
	Divider,
	Paper,
} from '@mui/material'
import { makeStyles, styled } from '@mui/material/styles'
import React from 'react'
import {
	ExpandMore,
	Event,
	LocationOn,
	AttachMoney,
	People,
	Schedule,
	Info
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import '../style.scss'

const PREFIX = 'OverviewScreen'

const classes = {
	body: `${PREFIX}-body`,
}

const Root = styled('div')({
	[`& .${classes.body}`]: {
		flexDirection: 'column',
	},
})

const OverviewScreen = ({ eventId }) => {
	const eventReducer = useSelector((state) => state.eventReducer)
	const events = eventReducer.events
	const currEvent = events.find((e) => e.eventId === eventId)

	const getEventStatus = (event) => {
		const now = new Date()
		const startDate = new Date(event?.fromDate)
		const endDate = new Date(event?.toDate)

		if (now < startDate) return { status: 'Upcoming', color: 'primary' }
		if (now >= startDate && now <= endDate) return { status: 'Ongoing', color: 'success' }
		return { status: 'Completed', color: 'default' }
	}

	const eventStatus = getEventStatus(currEvent)

	return (
		<Root className="screen overview-screen">
			{/* Header Section */}
			<Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h4" component="h1" fontWeight="bold">
						{currEvent?.title}
					</Typography>
					<Chip
						label={eventStatus.status}
						color={eventStatus.color}
						variant="filled"
						sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
					/>
				</Box>
				<Typography variant="h6" sx={{ opacity: 0.9 }}>
					Event Overview & Management
				</Typography>
			</Paper>

			{/* Quick Stats Cards */}
			<Grid container spacing={3} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6} md={3}>
					<Card sx={{ height: '100%', textAlign: 'center' }}>
						<CardContent>
							<Event color="primary" sx={{ fontSize: 40, mb: 1 }} />
							<Typography variant="h6" color="primary" fontWeight="bold">
								{currEvent?.registrationAmount || 0}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Registration Fee (₹)
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card sx={{ height: '100%', textAlign: 'center' }}>
						<CardContent>
							<People color="secondary" sx={{ fontSize: 40, mb: 1 }} />
							<Typography variant="h6" color="secondary" fontWeight="bold">
								{currEvent?.accomodationCategory ? Object.keys(currEvent.accomodationCategory).length : 0}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Accommodation Options
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card sx={{ height: '100%', textAlign: 'center' }}>
						<CardContent>
							<AttachMoney color="success" sx={{ fontSize: 40, mb: 1 }} />
							<Typography variant="h6" color="success.main" fontWeight="bold">
								{currEvent?.transportationAmount || 0}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Transportation (₹)
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card sx={{ height: '100%', textAlign: 'center' }}>
						<CardContent>
							<Schedule color="warning" sx={{ fontSize: 40, mb: 1 }} />
							<Typography variant="h6" color="warning.main" fontWeight="bold">
								{currEvent?.fromDate && currEvent?.toDate ?
									Math.ceil((new Date(currEvent.toDate) - new Date(currEvent.fromDate)) / (1000 * 60 * 60 * 24)) + 1 : 0
								}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Duration (Days)
							</Typography>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Detailed Information */}
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<Accordion defaultExpanded>
						<AccordionSummary
							expandIcon={<ExpandMore />}
							aria-controls="basic-info-content"
							id="basic-info-header"
						>
							<Box display="flex" alignItems="center">
								<Event sx={{ mr: 1, color: 'primary.main' }} />
								<Typography variant="h6">Basic Information</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails className={classes.body}>
							<Box display="flex" alignItems="center" mb={2}>
								<Schedule sx={{ mr: 1, color: 'text.secondary' }} />
								<Typography variant="body1">
									<strong>Date:</strong> {currEvent?.fromDate} to {currEvent?.toDate}
								</Typography>
							</Box>
							<Box display="flex" alignItems="center" mb={2}>
								<LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
								<Typography variant="body1">
									<strong>Venue:</strong> {currEvent?.venue}
								</Typography>
							</Box>
							{currEvent?.festType && (
								<Box display="flex" alignItems="center">
									<Info sx={{ mr: 1, color: 'text.secondary' }} />
									<Typography variant="body1">
										<strong>Event Type:</strong> {currEvent.festType === 1 ? 'Festival' : 'Camp'}
									</Typography>
								</Box>
							)}
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid item xs={12} md={6}>
					<Accordion defaultExpanded>
						<AccordionSummary
							expandIcon={<ExpandMore />}
							aria-controls="pricing-content"
							id="pricing-header"
						>
							<Box display="flex" alignItems="center">
								<AttachMoney sx={{ mr: 1, color: 'success.main' }} />
								<Typography variant="h6">Pricing Details</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails className={classes.body}>
							<Box mb={2}>
								<Typography variant="body1" mb={1}>
									<strong>Registration Fee:</strong> ₹{currEvent?.registrationAmount || 0}
								</Typography>
								<Typography variant="body1" mb={1}>
									<strong>Transportation:</strong> ₹{currEvent?.transportationAmount || 0}
								</Typography>
								{currEvent?.transportationDescription && (
									<Typography variant="body2" color="text.secondary" mb={2}>
										{currEvent.transportationDescription}
									</Typography>
								)}
							</Box>

							{currEvent?.accomodationCategory && (
								<Box>
									<Typography variant="subtitle2" gutterBottom>
										<strong>Accommodation Options:</strong>
									</Typography>
									{Object.keys(currEvent.accomodationCategory).map((key, index) => (
										<Chip
											key={index}
											label={`${currEvent.accomodationCategory[key].name} - ₹${currEvent.accomodationCategory[key].price || 0}`}
											variant="outlined"
											size="small"
											sx={{ mr: 1, mb: 1 }}
										/>
									))}
								</Box>
							)}
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid item xs={12}>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMore />}
							aria-controls="additional-content"
							id="additional-header"
						>
							<Box display="flex" alignItems="center">
								<Info sx={{ mr: 1, color: 'info.main' }} />
								<Typography variant="h6">Additional Information</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails className={classes.body}>
							{currEvent?.eligibility && (
								<Box mb={2}>
									<Typography variant="subtitle2" gutterBottom color="primary">
										<strong>Eligibility:</strong>
									</Typography>
									<Typography variant="body2">
										{currEvent.eligibility}
									</Typography>
								</Box>
							)}
							{currEvent?.recommendation && (
								<Box>
									<Typography variant="subtitle2" gutterBottom color="primary">
										<strong>Recommendations:</strong>
									</Typography>
									<Typography variant="body2">
										{currEvent.recommendation}
									</Typography>
								</Box>
							)}
						</AccordionDetails>
					</Accordion>
				</Grid>
			</Grid>

			{/* Action Buttons */}
			<Box mt={4} display="flex" gap={2} justifyContent="center">
				<Button variant="contained" color="primary" size="large">
					Edit Event
				</Button>
				<Button variant="outlined" color="secondary" size="large">
					View Registrations
				</Button>
				<Button variant="outlined" color="success" size="large">
					Export Data
				</Button>
			</Box>
		</Root>
	)
}

export default OverviewScreen
